import { useSelector } from "react-redux"
import { addCustomCssVars } from "../EntryHelpers/addCustomCssVars"
import getContrastBgColor from "../functions/getContrastBgColor"
import { useEffect } from "react"

const computeBevelBorderRadius = (corners, r) => {
    if (!corners || corners.length === 0 || !r) return '0px 0px 0px 0px'
    const tl = corners.includes('top_left')     ? r : '0px'
    const tr = corners.includes('top_right')    ? r : '0px'
    const br = corners.includes('bottom_right') ? r : '0px'
    const bl = corners.includes('bottom_left')  ? r : '0px'
    return `${tl} ${tr} ${br} ${bl}`
}

const computeBevelPolygon = (corners, r) => {
    if (!corners || corners.length === 0 || !r) return 'none'
    const tl = corners.includes('top_left')
    const tr = corners.includes('top_right')
    const br = corners.includes('bottom_right')
    const bl = corners.includes('bottom_left')
    const pts = []
    if (tl) pts.push(`${r} 0`)
    else pts.push(`0 0`)
    if (tr) { pts.push(`calc(100% - ${r}) 0`); pts.push(`100% ${r}`) }
    else pts.push(`100% 0`)
    if (br) { pts.push(`100% calc(100% - ${r})`); pts.push(`calc(100% - ${r}) 100%`) }
    else pts.push(`100% 100%`)
    if (bl) { pts.push(`${r} 100%`); pts.push(`0 calc(100% - ${r})`) }
    else pts.push(`0 100%`)
    if (tl) pts.push(`0 ${r}`)
    return `polygon(${pts.join(', ')})`
}

const getCssVars = (
    theme,
    custom_font_color,
    custom_font_color_color,
    header_appearance
) => {
    const {
        accent_color,
        accent_color_background,
        accent_color_dark,
        accent_color_footer_menu,
        accent_color_gradient,
        accent_color_titles,
        base_accent_color_footer_menu,
        border_radius_buttons,
        border_radius_posters,
        button_background_color,
        button_background_color_hover,
        button_border_style,
        button_border_style_hover,
        button_shadow,
        button_shadow_hover,
        custom_background,
        custom_button_color,
        custom_button_color_gradient,
        custom_button_color_gradient_hover,
        custom_button_color_hover,
        quick_ticket_button_background_color,
        quick_ticket_button_background_color_hover,
        quick_ticket_button_font_color,
        quick_ticket_button_text_color_hover,
        theme_colors,
        overview_filter_style,
        gradient_overwrites_accent_color,
        userbar_color,
        userbar_font_color,
        multitotal_style,
        slider_background_color,
        arthouse_variant,
        movietimes_styling,
        movietimes_styling_color,
        has_bevel_corners,
        bevel_corners,
        bevel_radius_small,
        bevel_radius_medium,
        bevel_radius_large,
        border_color_semi,
        sepia_filter_enabled,
        sepia_intensity_light,
        sepia_intensity_medium,
        sepia_intensity_heavy,
        sepia_heavy_grayscale,
        poster_texture_image,
        poster_texture_opacity,
        slider_texture_image,
        slider_dots_square,
        quickticket_visible,
        quick_ticket_background,
        quick_ticket_select_background,
        quick_ticket_select_text_color,
        quick_ticket_select_option_hover_background,
        quick_ticket_select_option_hover_color,
    } = theme

    const bevelEnabled = has_bevel_corners && bevel_corners && bevel_corners.length > 0

    const bevelClipSmall  = bevelEnabled ? computeBevelPolygon(bevel_corners, bevel_radius_small)  : 'none'
    const bevelClipMedium = bevelEnabled ? computeBevelPolygon(bevel_corners, bevel_radius_medium) : 'none'
    const bevelClipLarge  = bevelEnabled ? computeBevelPolygon(bevel_corners, bevel_radius_large)  : 'none'

    const sepiaLight  = sepia_filter_enabled ? `sepia(${sepia_intensity_light})`  : 'none'
    const sepiaMedium = sepia_filter_enabled ? `sepia(${sepia_intensity_medium})` : 'none'
    const sepiaHeavy  = sepia_filter_enabled
        ? `sepia(${sepia_intensity_heavy}) grayscale(${sepia_heavy_grayscale})`
        : 'none'

    const sliderTextureLayer = slider_texture_image
        ? `url('${slider_texture_image}')`
        : 'linear-gradient(transparent, transparent)'

    const getArthouseVariantSliderColor =  arthouse_variant === "dark" ? "#222121" : "#F2F2F2"

    let cssVars = {
        "--pmkinovar-accent_color" : accent_color,
        "--pmkinovar-userbar_color" : userbar_color ? userbar_color : accent_color_background,
        "--pmkinovar-accent_color_background" : accent_color_background,
        "--pmkinovar-accent_color_dark" : accent_color_dark,
        "--pmkinovar-accent_color_footer_menu" : accent_color_footer_menu ? accent_color_footer_menu : base_accent_color_footer_menu,
        "--pmkinovar-accent_color_gradient" : accent_color_gradient,
        "--pmkinovar-accent_color_titles" : accent_color_titles,
        "--pmkinovar-base_accent_color_footer_menu" : base_accent_color_footer_menu,
        "--pmkinovar-border_radius_buttons" : border_radius_buttons,
        "--pmkinovar-border_radius_posters" : border_radius_posters,
        "--pmkinovar-custom_background" : custom_background ? custom_background : accent_color_background,
        "--pmkinovar-quick_ticket_button_background_color" : quick_ticket_button_background_color,
        "--pmkinovar-quick_ticket_button_background_color_hover" : quick_ticket_button_background_color_hover,
        "--pmkinovar-quick_ticket_button_font_color" : quick_ticket_button_font_color,
        "--pmkinovar-quick_ticket_button_text_color_hover" : quick_ticket_button_text_color_hover,
        "--pmkinovar-theme_colors" : theme_colors,
        "--pmkinovar-button_shadow" : button_shadow,
        "--pmkinovar-button_shadow_hover" : button_shadow_hover,
        "--pmkinovar-gradient_overwrites_accent_color" : gradient_overwrites_accent_color ? gradient_overwrites_accent_color : accent_color,
        // old default colors
        "--pmkinovar-color-blue-prices" : '#c40000',
        "--pmkinovar-color-trailer-typo": "#FFF",
        "--pmkinovar-color-fsk-typo": "#181818",
        "--pmkinovar-color-fsk-typo-inverted": "#fff",
        "--pmkinovar-color-fsk-0": "#FFFFFF",
        "--pmkinovar-color-fsk-3": "#f37a24",
        "--pmkinovar-color-fsk-6": "#F0EC10",
        "--pmkinovar-color-fsk-8": "#0064a5",
        "--pmkinovar-color-fsk-10": "#c766b7",
        "--pmkinovar-color-fsk-12": "#31AE3F",
        "--pmkinovar-color-fsk-14": "#8a32d1",
        "--pmkinovar-color-fsk-15": "#f39200",
        "--pmkinovar-color-fsk-16": "#36A2DC",
        "--pmkinovar-color-fsk-18": "#E51A23",
        "--pmkinovar-color-fsk-0-transparency": "#FFFFFFbf",
        "--pmkinovar-color-fsk-3-transparency": "#f37a24bf",
        "--pmkinovar-color-fsk-6-transparency": "#F0EC10bf",
        "--pmkinovar-color-fsk-8-transparency": "#0064a5bf",
        "--pmkinovar-color-fsk-10-transparency": "#c766b7bf",
        "--pmkinovar-color-fsk-12-transparency": "#31AE3Fbf",
        "--pmkinovar-color-fsk-14-transparency": "#8a32d1bf",
        "--pmkinovar-color-fsk-15-transparency": "#f39200bf",
        "--pmkinovar-color-fsk-16-transparency": "#36A2DCbf",
        "--pmkinovar-color-fsk-18-transparency": "#E51A23bf",
        // Bevel
        "--pmkinovar-bevel-clip-small"    : bevelClipSmall,
        "--pmkinovar-bevel-clip-medium"   : bevelClipMedium,
        "--pmkinovar-bevel-clip-large"    : bevelClipLarge,
        "--pmkinovar-bevel-radius-small"  : bevelEnabled ? bevel_radius_small  : '0px',
        "--pmkinovar-bevel-radius-medium" : bevelEnabled ? bevel_radius_medium : '0px',
        "--pmkinovar-bevel-radius-large"  : bevelEnabled ? bevel_radius_large  : '0px',
        "--pmkinovar-bevel-border-radius-small"  : bevelEnabled ? computeBevelBorderRadius(bevel_corners, bevel_radius_small)  : '0px 0px 0px 0px',
        "--pmkinovar-bevel-border-radius-medium" : bevelEnabled ? computeBevelBorderRadius(bevel_corners, bevel_radius_medium) : '0px 0px 0px 0px',
        "--pmkinovar-bevel-border-radius-large"  : bevelEnabled ? computeBevelBorderRadius(bevel_corners, bevel_radius_large)  : '0px 0px 0px 0px',
        // Sepia
        "--pmkinovar-sepia-light"  : sepiaLight,
        "--pmkinovar-sepia-medium" : sepiaMedium,
        "--pmkinovar-sepia-heavy"  : sepiaHeavy,
        // Border
        "--pmkinovar-border-color-semi" : border_color_semi || 'transparent',
        // Textures
        "--pmkinovar-poster-texture-url"     : poster_texture_image ? `url('${poster_texture_image}')` : 'none',
        "--pmkinovar-poster-texture-opacity" : poster_texture_image ? (poster_texture_opacity || '0.6') : '0',
        "--pmkinovar-slider-texture-layer"   : sliderTextureLayer,
        // Slider dots
        "--pmkinovar-slider-dots-radius" : slider_dots_square ? '0' : '50%',
        // QuickTicket visibility
        "--pmkinovar-quickticket-display" : quickticket_visible ? 'block' : 'none',
        // QuickTicket select styles (only output when set)
        ...(quick_ticket_background                     && { "--pmkinovar-quickticket-background"         : quick_ticket_background }),
        ...(quick_ticket_select_background              && { "--pmkinovar-quickticket-select-bg"           : quick_ticket_select_background }),
        ...(quick_ticket_select_text_color              && { "--pmkinovar-quickticket-select-text"         : quick_ticket_select_text_color }),
        ...(quick_ticket_select_option_hover_background && { "--pmkinovar-quickticket-option-hover-bg"     : quick_ticket_select_option_hover_background }),
        ...(quick_ticket_select_option_hover_color      && { "--pmkinovar-quickticket-option-hover-text"   : quick_ticket_select_option_hover_color }),
    }

    //LIGHT THEME
    if(theme_colors === "Light") {
        cssVars = {
            ...cssVars,
            "--pmkinovar-custom_font_color" : custom_font_color && custom_font_color_color ? custom_font_color_color : '#000000',
            "--pmkinovar-userbar_font_color" : userbar_font_color ? userbar_font_color : custom_font_color && custom_font_color_color ? custom_font_color_color : '#000000',
            "--pmkinovar-custom_button_color" : button_background_color,
            "--pmkinovar-custom_button_color_hover" : button_background_color_hover,
            "--pmkinovar-button_background_color" : 'transparent',
            "--pmkinovar-button_background_color_hover" : '#ffffff59',
            "--pmkinovar-button_border_style" : button_border_style ? button_border_style : '1px solid ' + button_background_color,
            "--pmkinovar-button_border_style_hover" : button_border_style_hover ? button_border_style_hover : '1px solid ' + button_background_color_hover,
            "--pmkinovar-filter_button_background_color" : 'transparent',
            "--pmkinovar-slider-background-default-color": getArthouseVariantSliderColor,
            "--pmkinovar-slider-background-colored-color": multitotal_style === 'colored' ? getArthouseVariantSliderColor : slider_background_color ? slider_background_color : "#181818",
            // old default colors
            "--pmkinovar-color-border-transparent" :'#18181833',
            "--pmkinovar-color-selection-main" :'#cecccc',
            "--pmkinovar-color-theme-main-transparent": "#ffffff80",
            "--pmkinovar-color-theme-main-more-transparent": "#ffffff59",
            "--pmkinovar-color-theme-selektor-transparent": "#ffffff45",
            "--pmkinovar-color-theme-main-contrast-light-transparent": "#0000001a",
            //needs to be like this for rgba reasons - if varaible is used please write rgb() around
            "--pmkinovar-color-theme-main": "255, 255, 255",
            "--pmkinovar-color-theme-main-lighter": "#b3b2b2",
            "--pmkinovar-color-theme-main-minor-transparent": "#e4e7e8cc",
            "--pmkinovar-color-theme-main-lighter-transparent": "#e4e7e866",
            "--pmkinovar-color-theme-main-contrast": "#000000",
            "--pmkinovar-color-theme-main-contrast-transparent": "#00000066",
            "--pmkinovar-color-theme-main-contrast-minor-transparent": "#000000bf",
            "--pmkinovar-color-typo": "#000000",
            "--pmkinovar-color-typo-large-slider": '#FFFFFF',
            "--pmkinovar-color-link-inactive": "#8f8e8e",
            "--pmkinovar-color-button-text": "#fff",
            "--pmkinovar-color-border": "#161616",
            "--pmkinovar-color-typo-muted": "#363636",
            "--pmkinovar-color-no-image": "#a7a7a7",
            "--pmkinovar-accent-color-background-dark-color": "#000000",
            "--pmkinovar-attribute-fill-color": "#000000",
            "--pmkinovar-attribute-movietimes-fill-color": "#000000",
            "--pmkinovar-showtimes-fill-color": "#000000",
            "--pmkinovar-attribute-movietimes-fill-color-invert": '#FFFFFF',
            "--pmkinovar-showtimes-fill-color-invert": '#FFFFFF',
            "--pmkinovar-movietimes-styling" : movietimes_styling ? movietimes_styling : 'transparent',
            "--pmkinovar-movietimes-styling-color" : movietimes_styling_color ? movietimes_styling_color : '#000000',
            "--pmkinovar-movietimes-styling-theater-color" : movietimes_styling_color ? movietimes_styling_color : '#161616'
        }
    //ARTHOUSE THEME
    } else if (theme_colors === "Arthouse") {
        cssVars = {
            ...cssVars,
            "--pmkinovar-custom_button_color" : custom_button_color ? custom_button_color : button_background_color_hover,
            "--pmkinovar-custom_button_color_hover" : custom_button_color_hover ? custom_button_color_hover : button_background_color,
            "--pmkinovar-custom_font_color" : custom_font_color && custom_font_color_color ? custom_font_color_color : arthouse_variant === "dark" ? "#FFFFFF" : '#000000',
            "--pmkinovar-userbar_font_color" : userbar_font_color ? userbar_font_color : custom_font_color && custom_font_color_color ? custom_font_color_color : arthouse_variant === "dark" ? "#FFFFFF" : '#000000',
            "--pmkinovar-button_background_color" : button_background_color,
            "--pmkinovar-button_background_color_hover" : button_background_color_hover,
            "--pmkinovar-button_border_style" : button_border_style ? button_border_style : '1px solid ' + button_background_color,
            "--pmkinovar-button_border_style_hover" : button_border_style_hover ? button_border_style_hover : '1px solid ' + button_background_color,
            "--pmkinovar-filter_button_background_color" : custom_button_color_gradient ? custom_button_color_gradient : button_background_color,
            "--pmkinovar-slider-background-default-color": getArthouseVariantSliderColor,
            "--pmkinovar-slider-background-colored-color": multitotal_style === 'colored' ? getArthouseVariantSliderColor : slider_background_color ? slider_background_color : "#181818",
            // old default colors
            "--pmkinovar-color-border-transparent" : arthouse_variant === "dark" ? '#ededed33' : '#18181833',
            "--pmkinovar-color-selection-main" : arthouse_variant === "dark" ? '#5c5c5c' : '#cecccc',
            "--pmkinovar-color-theme-main-transparent": arthouse_variant === "dark" ? "#ededed33" : "#ffffff80",
            "--pmkinovar-color-theme-main-more-transparent": arthouse_variant === "dark" ? '#15151559' : "#ffffff59",
            "--pmkinovar-color-theme-selektor-transparent": "#ffffff45",
            "--pmkinovar-color-theme-main-contrast-light-transparent": "#1818181a",
            //needs to be like this for rgba reasons - if varaible is used please write rgb() around
            "--pmkinovar-color-theme-main": arthouse_variant === 'dark' ? '0, 0, 0' :  "255, 255, 255",
            "--pmkinovar-color-theme-main-lighter": arthouse_variant === 'dark' ? '#232323' : "#dfdfdf",
            "--pmkinovar-color-theme-main-minor-transparent":  arthouse_variant === 'dark' ? "#000000cc" : "#ffffffcc",
            "--pmkinovar-color-theme-main-lighter-transparent": arthouse_variant === 'dark' ? "#33363766" : "#e4e7e866",
            "--pmkinovar-color-theme-main-contrast": arthouse_variant === 'dark' ? '#f5f5f5ff' : "#181818",
            "--pmkinovar-color-theme-main-contrast-transparent": arthouse_variant === 'dark' ? '#f5f5f566' : "#18181866",
            "--pmkinovar-color-theme-main-contrast-minor-transparent":arthouse_variant === 'dark' ? '#f5f5f5a6' : "#181818a6",
            "--pmkinovar-color-typo": arthouse_variant === "dark" ? '#FFFFFF' : "#181818",
            "--pmkinovar-color-typo-large-slider": '#FFFFFF',
            "--pmkinovar-color-link-inactive": "#8f8e8e",
            "--pmkinovar-color-button-text": "#000000",
            "--pmkinovar-color-border": "#161616",
            "--pmkinovar-color-typo-muted": "#363636",
            "--pmkinovar-color-no-image": "#F2F2F2",
            "--pmkinovar-accent-color-background-dark-color": arthouse_variant === "dark" ? '#FFFFFF' : "#181818",
            "--pmkinovar-attribute-fill-color": arthouse_variant === "dark" ? '#FFFFFF' : "#181818",
            "--pmkinovar-attribute-movietimes-fill-color": "#181818",
            "--pmkinovar-showtimes-fill-color": "#181818",
            "--pmkinovar-attribute-movietimes-fill-color-invert": arthouse_variant === "dark" ? '#FFFFFF' : "#181818",
            "--pmkinovar-showtimes-fill-color-invert": arthouse_variant === "dark" ? '#FFFFFF' : "#181818",
            "--pmkinovar-movietimes-styling" : movietimes_styling ? movietimes_styling : '#FFFFFF',
            "--pmkinovar-movietimes-styling-color" : movietimes_styling_color ? movietimes_styling_color : '#000000',
            "--pmkinovar-movietimes-styling-theater-color" : movietimes_styling_color ? movietimes_styling_color : '#161616'
        }

    // OTHER THEMES (DARK/KMOTION)
    } else {
        if(overview_filter_style === 'clean') {
            cssVars = {
                ...cssVars,
                "--pmkinovar-filter_button_background_color" : 'transparent'
            }
        } else {
            cssVars = {
                ...cssVars,
                "--pmkinovar-filter_button_background_color" : custom_button_color_gradient ? custom_button_color_gradient : button_background_color
            }
        }

        cssVars = {
            ...cssVars,
            "--pmkinovar-custom_font_color" : custom_font_color && custom_font_color_color ? custom_font_color_color : '#FFFFFF',
            "--pmkinovar-userbar_font_color" : userbar_font_color ? userbar_font_color : custom_font_color && custom_font_color_color ? custom_font_color_color : '#FFFFFF',
            "--pmkinovar-custom_button_color" : custom_button_color ? custom_button_color : custom_font_color && custom_font_color_color ? custom_font_color_color : '#FFFFFF',
            "--pmkinovar-custom_button_color_hover" : custom_button_color_hover ? custom_button_color_hover : custom_font_color && custom_font_color_color ? custom_font_color_color : '#FFFFFF',
            "--pmkinovar-button_background_color" : custom_button_color_gradient ? custom_button_color_gradient : button_background_color,
            "--pmkinovar-button_background_color_hover" : custom_button_color_gradient_hover ? custom_button_color_gradient_hover : button_background_color_hover,
            "--pmkinovar-button_border_style" : button_border_style && button_border_style,
            "--pmkinovar-button_border_style_hover" : button_border_style_hover && button_border_style_hover,
            "--pmkinovar-slider-background-default-color": getArthouseVariantSliderColor,
            "--pmkinovar-slider-background-colored-color": multitotal_style === 'colored' ? getArthouseVariantSliderColor : slider_background_color ? slider_background_color : "#181818",
            // old default colors
            "--pmkinovar-color-border-transparent" :'#ededed33',
            "--pmkinovar-color-selection-main" :'#2d2d2d',
            "--pmkinovar-color-theme-main-transparent": "#00000080",
            "--pmkinovar-color-theme-main-more-transparent": "#00000059",
            "--pmkinovar-color-theme-selektor-transparent": "#00000045",
            "--pmkinovar-color-theme-main-contrast-light-transparent": "#ffffff1a",
            //needs to be like this for rgba reasons - if varaible is used please write rgb() around
            "--pmkinovar-color-theme-main": "0, 0, 0",
            "--pmkinovar-color-theme-main-lighter": "#191919",
            "--pmkinovar-color-theme-main-minor-transparent": "#1d1d1dcc",
            "--pmkinovar-color-theme-main-lighter-transparent": "#3b3b3b80",
            "--pmkinovar-color-theme-main-contrast": "#FFFFFF",
            "--pmkinovar-color-theme-main-contrast-transparent": "#ffffff66",
            "--pmkinovar-color-theme-main-contrast-minor-transparent": "#000000bf",
            "--pmkinovar-color-typo": "#ffffff",
            "--pmkinovar-color-typo-large-slider": '#FFFFFF',
            "--pmkinovar-color-link-inactive": "#626262",
            "--pmkinovar-color-button-text": "#fff",
            "--pmkinovar-color-border": "#ededed",
            "--pmkinovar-color-typo-muted": "#C9C9C9",
            "--pmkinovar-color-no-image": "#2d2d2d",
            "--pmkinovar-accent-color-background-dark-color": "#FFFFFF",
            "--pmkinovar-attribute-fill-color": "#FFFFFF",
            "--pmkinovar-attribute-movietimes-fill-color": "#FFFFFF",
            "--pmkinovar-showtimes-fill-color": "#FFFFFF",
            "--pmkinovar-attribute-movietimes-fill-color-invert": '#000000',
            "--pmkinovar-showtimes-fill-color-invert": '#000000',
            "--pmkinovar-movietimes-styling" : movietimes_styling ? movietimes_styling : 'transparent',
            "--pmkinovar-movietimes-styling-color" : movietimes_styling_color ? movietimes_styling_color : '#FFFFFF',
            "--pmkinovar-movietimes-styling-theater-color" : movietimes_styling_color ? movietimes_styling_color : '#ededed'
        }
    }

    if(header_appearance === "partly_filled_with_color") {
        cssVars = {
            ...cssVars,
            "--pmkinovar-borlabs_accent_color_background" : accent_color_gradient ? accent_color_gradient :accent_color_background
        }
    } else {
        cssVars = {
            ...cssVars,
            "--pmkinovar-borlabs_accent_color_background" : accent_color_background
        }
    }

    return cssVars
}

const getCssVarsAccess = (
    theme
) => {

    const {
        accent_color_background,
        custom_background,
        theme_colors,
        arthouse_variant
    } = theme

    const bgColor = custom_background ? custom_background : accent_color_background

    let fgColor
    let defColor
    
    if(theme_colors === "Light") {
        fgColor = '#000000'
        defColor = "#ffffff"
    } else if (theme_colors === "Arthouse") {
        fgColor = arthouse_variant === "dark" ? '#FFFFFF' : "#181818"
        defColor = arthouse_variant === "dark" ? '#000000"' : '#FFFFFF'
    } else {
        fgColor = "#ffffff"
        defColor = "#000000"
    }


    const bg = getContrastBgColor(bgColor, fgColor, defColor)

    const cssVars = {
        '--pmkinovar-access_color_typo': 'rgb(29, 29, 29)',
        '--pmkinovar-access_color_typo-inverse': '#fff',
        '--pmkinovar-access_color_background_theme': bg,
        '--pmkinovar-access_color_border': 'rgb(104, 104, 104)',
        '--pmkinovar-access_color_focus': 'rgb(0, 51, 102)',
        '--pmkinovar-access_background_focus': 'rgb(221, 238, 255)',
        '--pmkinovar-access_color_background': '#eee',
        '--pmkinovar-access_accent_color_dark': '#005a9c',
        '--pmkinovar-access_accewnt_color_dark_trans': 'rgb(0 90 156 / 75%)',
        '--pmkinovar-access_accent_color_medium': '#ccc',
        '--pmkinovar-access_accent_color_light': '#ddd'
    }

    return cssVars
}


const setStyles = (            
    styleId,
    isAccesTheme,
    theme,
    custom_font_color,
    custom_font_color_color,
    header_appearance
) => {
    const themeJS = theme.toJS()
    let cssVars = getCssVars(
        themeJS,
        custom_font_color,
        custom_font_color_color,
        header_appearance
    )

    if(isAccesTheme) {
        const cssVarsAccess = getCssVarsAccess(themeJS)

        cssVars = {
            ...cssVars,
            ...cssVarsAccess
        }
    }

    addCustomCssVars(styleId, cssVars)  
}



export const useRootCssVars = (isAccesTheme) => {
    const theme = useSelector(state => state.getIn(['cinemaData', 'theme']))
    const custom_font_color = useSelector(state => state.getIn(['cinemaData', 'front', 'custom_font_color']))
    const custom_font_color_color = useSelector(state => state.getIn(['cinemaData', 'front', 'custom_font_color_color']))
    const header_appearance = useSelector(state => state.getIn(['cinemaData', 'front', 'header_appearance']))

    const styleId = 'pmkino-custom-css-styles'

    useEffect(() => {
        setStyles(
            styleId,
            isAccesTheme,
            theme,
            custom_font_color,
            custom_font_color_color,
            header_appearance
        )

    }, [isAccesTheme])

    return null
}